import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";
import { Button } from "@carbon/react";
import { FilePreview } from "../../components";
import { DeliveryInterface } from "../../types";

export interface PurchaseOrderFilePanelProps {
  delivery: DeliveryInterface;
  canUploadPoFile: boolean;
  uploadError: string | null;
  uploadedSpecificationFileUrl?: string;
  isUploading: boolean;
  isUpdatingFileUrl: boolean;
  onFileUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  onUpdateFileUrl: () => void;
}

/**
 * The purchase-order-file column. When the delivery already carries a
 * `purchaseOrderFile` it previews it (for everyone, regardless of ability). Otherwise
 * the upload dropzone renders only when `canUploadPoFile` (the viewer holds
 * `MANAGE_DELIVERIES`, buyer-only); anyone else gets nothing — read-only. The upload contract (`useUpdateDeliveryFileUrl`) is untouched: this component
 * only renders the controls and delegates every mutation to the parent's handlers.
 */
const PurchaseOrderFilePanel = ({
  delivery,
  canUploadPoFile,
  uploadError,
  uploadedSpecificationFileUrl,
  isUploading,
  isUpdatingFileUrl,
  onFileUpload,
  onUpdateFileUrl,
}: PurchaseOrderFilePanelProps) => {
  const { t } = useTranslation();

  if (delivery.purchaseOrderFile) {
    return (
      <FilePreview
        url={delivery.purchaseOrderFile}
        fileName={delivery.purchaseOrderNumber}
      />
    );
  }

  if (!canUploadPoFile) return null;

  return (
    <div className="delivery-file">
      <h3 className="delivery-file__heading">
        {t("deliveries.form.purchaseOrderFile")}
      </h3>

      {uploadError && <div className="delivery-file__error">{uploadError}</div>}

      <div className="delivery-file__dropzone">
        <div className="delivery-file__dropzone-inner">
          <svg
            className="delivery-file__icon"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {uploadedSpecificationFileUrl && (
            <p className="delivery-file__filename">
              {uploadedSpecificationFileUrl.split("/").pop()}
            </p>
          )}
          <label htmlFor="file-upload" className="delivery-file__upload-label">
            {isUploading ? (
              <ClipLoader loading size={20} />
            ) : (
              <span>{t("deliveries.form.upload")}</span>
            )}
            <input
              id="file-upload"
              name="file-upload"
              type="file"
              onChange={onFileUpload}
              disabled={isUploading || isUpdatingFileUrl}
            />
          </label>
          <p className="delivery-file__hint">PDF, PNG, JPG up to 10MB</p>
        </div>
      </div>

      {uploadedSpecificationFileUrl && (
        <div className="delivery-file__actions">
          <Button
            size="md"
            onClick={onUpdateFileUrl}
            disabled={isUpdatingFileUrl}
          >
            {isUpdatingFileUrl
              ? t("deliveries.updating")
              : t("deliveries.updateFile")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderFilePanel;
